import React, { useCallback, useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { withRouter } from "react-router";
import { Redirect } from "react-router-dom";
import { RootStoreContext } from "src/stores/RootStore";
import { selectRoute, Route } from "src/config/routes";
import { RealestateVisaList } from "./userField/list/RealestateVisaList";
import { STATE } from "src/stores/krediflow/RealestateVisaStore";
import styles from "./RealestateVisas.module.css";
import { useSaveFormByShortcut } from "src/hooks/form/useSaveFormByShortcut";
import { ColorStyle } from "src/utils/Colors";

const RealestateVisasBase = () => {
    const { t } = useTranslation();
    const { navStore, realestateVisaStore } = useContext(RootStoreContext);

    useEffect(() => {
        navStore.setTitle(t(""));
        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);
        navStore.setBackbutton(
            selectRoute(Route.krediflowConfigurationSettings),
            t("sidebar.settings.title").toString()
        );
    }, [navStore, t]);

    useEffect(() => {
        async function initializeRealestateVisaData() {
            await realestateVisaStore.init();
        }
        initializeRealestateVisaData();
        // eslint-disable-next-line
    }, []);

    useSaveFormByShortcut({
        saveFormFunction: realestateVisaStore.saveRealestateVisas,
        isFormDirty: realestateVisaStore.hasChanges ?? false
    });

    const onSearchRealestateVisaEntries = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const currentSearchQuery = e.target.value;

            realestateVisaStore.setRealestateVisaSearchQuery(currentSearchQuery);
        },
        [realestateVisaStore]
    );

    const saveRealestateUsersLabel = (
        <div className="d-flex align-items-center">
            <div>{t("labels.save")}</div>
            <UI.RotatingSpinner
                className={`${styles.SaveButtonSpinner} ${
                    realestateVisaStore.currentState !== STATE.SAVING ? "d-none" : ""
                }`}
                noLogo={true}
                size={20}
            />
        </div>
    );

    const onSaveRealestateVisas = useCallback(async () => {
        await realestateVisaStore.saveRealestateVisas();
    }, [realestateVisaStore]);

    const onResetRealestateVisas = useCallback(async () => {
        await realestateVisaStore.resetRealestateVisaList();
    }, [realestateVisaStore]);

    if (!navStore.hasFeatureKrediFlowPro) {
        return <Redirect to={selectRoute(Route.krediflowConfigurationSettings)} />;
    }

    return (
        <UI.Container className="pt-4">
            <UI.Row>
                <UI.Col xs={12}>
                    <UI.Title>{t("screens.kredi_flow_settings.visaworkflow.title")}</UI.Title>
                </UI.Col>
            </UI.Row>

            <UI.Row>
                <UI.Col xs={12} md={4} className="mb-3">
                    <div className={styles.RealestateVisaSearchContainer}>
                        <UI.Icon icon={UI.SVGIcon.Search} className="mr-1" color={ColorStyle("gray")} size="small" />
                        <input
                            onChange={onSearchRealestateVisaEntries}
                            type="text"
                            value={realestateVisaStore.realestateVisaSearchQuery}
                            className={styles.RealestateVisaSearchInput}
                        />
                    </div>
                </UI.Col>
            </UI.Row>
            <UI.Row>
                <UI.Col xs={12}>
                    <RealestateVisaList realestateVisaItems={realestateVisaStore.filteredRealestateVisaEntries} />
                </UI.Col>
            </UI.Row>
            <UI.Row className="mb-4">
                <UI.Col className={"flex-grow-0"}>
                    <UI.Button
                        onClick={onSaveRealestateVisas}
                        className={`mr-2 ${styles.RealestateVisaButton}`}
                        label={saveRealestateUsersLabel}
                        disabled={!realestateVisaStore.hasChanges || realestateVisaStore.currentState === STATE.SAVING}
                    />
                </UI.Col>
                <UI.Col>
                    <UI.Button
                        label={t("labels.discard")}
                        disabled={!realestateVisaStore.hasChanges || realestateVisaStore.currentState === STATE.SAVING}
                        onClick={onResetRealestateVisas}
                        className={`${styles.RealestateVisaButton} ${styles.DiscardButton} ${
                            !realestateVisaStore.hasChanges || realestateVisaStore.currentState === STATE.SAVING
                                ? styles.Disabled
                                : ""
                        }`}
                        variant="outline-primary"
                    />
                </UI.Col>
            </UI.Row>
        </UI.Container>
    );
};

export const RealestateVisas = withRouter(Sentry.withProfiler(observer(RealestateVisasBase)));
